import {Observability} from '@stripe-internal/conversations-client';

import {useApp} from '../contexts/AppContext';

export {useApp} from '../contexts/AppContext';

export function useObservability(): Observability {
  return useApp();
}

export function useAppStateSelector() {
  const app = useApp();
  const {useSelector} = app.stateStore;
  return useSelector;
}

export function useSetAppState() {
  const app = useApp();
  return app.stateStore.setValue;
}

export function useAppError() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.error);
}

export function useSetAppError(): (error: {
  message?: string | undefined;
  errorId?: string | undefined;
}) => void {
  const setAppState = useSetAppState();
  return (error) => {
    setAppState((state) => {
      state.error = error;
    });
  };
}

export function useCurrentConversationId() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.currentConversationId);
}

export function useCurrentConversation() {
  const app = useApp();
  const currentConversationId = useCurrentConversationId();
  if (!currentConversationId) {
    return null;
  }
  return app.getConversation(currentConversationId) ?? null;
}

export function useConversation() {
  const app = useApp();
  const currentConversationId = useCurrentConversationId();
  const error = useAppError();

  return {
    currentConversationId,
    error,
    startConversation: app.startConversation.bind(app),
    resumeOrStartConversation: app.resumeOrStartConversation.bind(app),
    restartConversation: app.restartConversation.bind(app),
    endConversation: app.endConversation.bind(app),
  };
}

export function useApi() {
  const app = useApp();
  return app.api;
}

export function useConfig() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.config);
}

export function useVisitor() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.profile.visitor);
}

export function useFlags() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.profile.featureFlags);
}

export function useHasFlag(flag: string): boolean {
  const app = useApp();
  return app.hasFlag(flag);
}

export function useAudience() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.config.audience);
}

export function useEntryPoint() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.config.entryPoint);
}

export function useTriggerParams() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.triggerParams);
}

export function useTriggerPoint() {
  const triggerParams = useTriggerParams();
  return triggerParams?.triggerPoint;
}

export function useVisibility() {
  const app = useApp();
  const useSelector = useAppStateSelector();
  const visibility = useSelector((state) => state.visibility);
  return {
    visibility,
    open: app.open,
    minimize: app.minimize,
    close: app.close,
  };
}

export function useLocale() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.config.locale);
}

export function useSTPBaseUrl() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.config.stripeThirdPartyBaseUrl);
}

export function useParentOrigin() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.config.parentOrigin);
}

export function useClient() {
  const app = useApp();
  return app.client;
}

export function useParentMessageBus() {
  const app = useApp();
  return app.parentMessageBus;
}

export function useShowCloseConfirmation() {
  const useSelector = useAppStateSelector();
  return useSelector((state) => state.showCloseConfirmation);
}
